import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, MessageSquare, TrendingUp, Clock, AlertCircle, Plus,
  ArrowUpRight, Zap, Radio, ChevronRight, Activity, Users,
  CheckCircle2, Circle, ArrowRight, Lightbulb, AlertTriangle,
  Sparkles, RefreshCw, ChevronDown, TrendingDown, ShoppingBag,
  BarChart3, Heart,
} from 'lucide-react';
import { agentsService } from '@/services/agents.service';
import { creditsService } from '@/services/credits.service';
import { knowledgeService } from '@/services/knowledge.service';
import { channelsService } from '@/services/channels.service';
import { useAuthStore } from '@/store/authStore';
import { DashboardStats, Agent, AgentStatus } from '@/types';
import { formatNumber, formatDuration, formatRelativeTime } from '@/utils/format';
import { cn } from '@/utils/cn';

interface SetupStep {
  key: string;
  label: string;
  description: string;
  href: string;
  check: (counts: { agents: number; knowledgeBases: number; channels: number }) => boolean;
}

const setupSteps: SetupStep[] = [
  {
    key: 'agent',
    label: 'Create your first agent',
    description: 'Build an AI agent to handle conversations',
    href: '/agents/new',
    check: (c) => c.agents > 0,
  },
  {
    key: 'knowledge',
    label: 'Add knowledge',
    description: 'Upload documents for your agent to learn from',
    href: '/knowledge',
    check: (c) => c.knowledgeBases > 0,
  },
  {
    key: 'channel',
    label: 'Connect a channel',
    description: 'Deploy your agent on Telegram, WhatsApp, or web',
    href: '/channels',
    check: (c) => c.channels > 0,
  },
];

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentAgents, setRecentAgents] = useState<Agent[]>([]);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [insight, setInsight] = useState<any>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightExpanded, setInsightExpanded] = useState(false);
  const [setupCounts, setSetupCounts] = useState({ agents: 0, knowledgeBases: 0, channels: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [statsData, agentsData, kbData, channelData] = await Promise.all([
          agentsService.getDashboardStats(),
          agentsService.getAgents(),
          knowledgeService.getKnowledgeBases().catch(() => []),
          channelsService.getChannels().catch(() => []),
        ]);
        setStats(statsData);
        setSetupCounts({
          agents: agentsData.length,
          knowledgeBases: kbData.length,
          channels: channelData.length,
        });
        setRecentAgents(
          agentsData.sort((a: Agent, b: Agent) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          ).slice(0, 4)
        );
        creditsService.getBalance().then(d => setCreditBalance(d.balance)).catch(() => {});
        agentsService.getLatestInsight().then(d => setInsight(d)).catch(() => {});
      } catch {
        setError('Failed to load dashboard data.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Skeleton header */}
        <div className="h-8 w-64 bg-gray-200 rounded-lg animate-pulse" />
        <div className="h-4 w-96 bg-gray-100 rounded animate-pulse" />
        {/* Skeleton bento */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-5 space-y-3">
              <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
              <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-24 bg-gray-50 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-6 h-64 animate-pulse" />
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-6 h-64 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-7 w-7 text-red-500" />
          </div>
          <p className="text-base font-semibold text-gray-900 mb-1">{error}</p>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getStatusDot = (status: AgentStatus) => {
    const colors: Record<AgentStatus, string> = {
      active: 'bg-emerald-500',
      testing: 'bg-amber-500',
      paused: 'bg-orange-400',
      draft: 'bg-gray-300',
      archived: 'bg-red-400',
    };
    return colors[status] || 'bg-gray-300';
  };

  const completedSteps = setupSteps.filter(s => s.check(setupCounts)).length;
  const setupPercent = Math.round((completedSteps / setupSteps.length) * 100);
  const showSetup = setupPercent < 100;

  const handleGenerateInsight = async () => {
    setInsightLoading(true);
    try {
      const data = await agentsService.generateInsight();
      setInsight(data);
    } catch {
      // silent fail
    } finally {
      setInsightLoading(false);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Greeting */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{greeting}, {firstName}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Here's what's happening with your AI workforce.</p>
        </div>
        <button
          onClick={() => navigate('/agents/new')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white text-sm font-medium rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 active:bg-blue-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Agent
        </button>
      </div>

      {/* Setup Checklist */}
      {showSetup && (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Get started</h3>
              <p className="text-xs text-gray-500 mt-0.5">Complete these steps to set up your workspace</p>
            </div>
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
              {completedSteps}/{setupSteps.length}
            </span>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 bg-gray-100 rounded-full mb-4 overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${setupPercent}%` }}
            />
          </div>
          <div className="space-y-2">
            {setupSteps.map((step) => {
              const done = step.check(setupCounts);
              return (
                <button
                  key={step.key}
                  onClick={() => !done && navigate(step.href)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                    done ? 'bg-gray-50' : 'hover:bg-blue-50 cursor-pointer'
                  )}
                >
                  {done ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      'text-sm font-medium',
                      done ? 'text-gray-400 line-through' : 'text-gray-900'
                    )}>
                      {step.label}
                    </p>
                    {!done && (
                      <p className="text-xs text-gray-500">{step.description}</p>
                    )}
                  </div>
                  {!done && <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div
          onClick={() => navigate('/agents')}
          className="group bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 cursor-pointer hover:border-blue-200 hover:shadow-[3px_3px_0px_#1a1a2e] transition-all duration-200"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Agents</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
              <Bot className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(stats?.total_agents || 0)}</p>
          <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
            {stats?.active_agents || 0} active
          </p>
        </div>

        <div
          onClick={() => navigate('/conversations')}
          className="group bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 cursor-pointer hover:border-blue-200 hover:shadow-[3px_3px_0px_#1a1a2e] transition-all duration-200"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Conversations</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
              <MessageSquare className="w-4 h-4 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(stats?.total_conversations || 0)}</p>
          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
            <Activity className="w-3 h-3" />
            {stats?.active_conversations || 0} active
          </p>
        </div>

        <div className="group bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 hover:border-amber-200 hover:shadow-[3px_3px_0px_#1a1a2e] transition-all duration-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Avg Response</span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
              <Zap className="w-4 h-4 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatDuration(stats?.avg_response_time || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">Response time</p>
        </div>

        <div className="group bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 hover:border-emerald-200 hover:shadow-[3px_3px_0px_#1a1a2e] transition-all duration-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customers</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
              <Users className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatNumber(stats?.total_customers || 0)}</p>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-emerald-500" />
            Growing
          </p>
        </div>
      </div>

      {/* ── Business Intelligence — "The Morning Briefing" ── */}
      {insight ? (() => {
        const s = insight.stats || {};
        const ins = insight.insights || {};
        const score = s.health_score ?? 0;
        const wow = s.wow || {};
        const scoreStroke = score >= 70 ? '#10B981' : score >= 40 ? '#F59E0B' : '#EF4444';
        const scoreBgLight = score >= 70 ? 'bg-emerald-50' : score >= 40 ? 'bg-amber-50' : 'bg-red-50';
        const circumference = 2 * Math.PI * 42;
        const dashOffset = circumference - (score / 100) * circumference;
        const capWow = (v: number) => Math.abs(v) > 999 ? (v > 0 ? '+999%' : '-999%') : (v > 0 ? `+${v}%` : `${v}%`);
        const maxCh = Math.max(...(s.channel_breakdown || []).map((c: any) => c.conversations), 1);

        return (
          <div className="space-y-3">
            {/* ═══ ROW 1: Health Gauge + Headline + WoW Stats ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-3">
              {/* Health Score Gauge */}
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 flex flex-col items-center justify-center min-w-[140px]">
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="8" />
                    <circle cx="50" cy="50" r="42" fill="none" stroke={scoreStroke} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset} className="transition-all duration-1000" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-black text-[#1a1a2e]">{score}</span>
                    <span className="text-[8px] uppercase tracking-widest text-gray-400 font-bold">health</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <button onClick={handleGenerateInsight} disabled={insightLoading} className="text-[9px] text-gray-400 hover:text-blue-500 transition-colors flex items-center gap-1">
                    <RefreshCw className={cn('w-2.5 h-2.5', insightLoading && 'animate-spin')} />
                    {insightLoading ? 'analyzing' : 'refresh'}
                  </button>
                </div>
              </div>

              {/* Headline + WoW Stats */}
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
                {/* Headline bar */}
                <div className={cn('px-4 py-3 border-b-2 border-[#1a1a2e]', scoreBgLight)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">
                          {new Date(insight.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(insight.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {insight.conversations_analyzed} conversations
                        </span>
                      </div>
                      <p className="text-[13px] font-semibold text-[#1a1a2e] leading-snug">{ins.headline || `${s.total_conversations || 0} conversations this week`}</p>
                    </div>
                    <button onClick={() => setInsightExpanded(!insightExpanded)} className="p-1 rounded-md hover:bg-black/5 transition-colors flex-shrink-0">
                      <ChevronDown className={cn('w-4 h-4 text-gray-500 transition-transform', insightExpanded && 'rotate-180')} />
                    </button>
                  </div>
                </div>

                {/* WoW Stats Strip */}
                <div className="grid grid-cols-4 divide-x divide-gray-100">
                  {[
                    { label: 'Conversations', val: s.total_conversations || 0, delta: wow.conversations_delta, color: 'text-[#1a1a2e]' },
                    { label: 'Resolved', val: s.resolved || 0, delta: wow.resolved_delta, color: 'text-emerald-600' },
                    { label: 'Customers', val: s.unique_customers || 0, delta: wow.customers_delta, color: 'text-[#1a1a2e]' },
                    { label: 'Escalated', val: s.escalated || 0, delta: wow.escalated_delta, color: 'text-red-500', inverted: true },
                  ].map((item, i) => {
                    const d = item.delta || 0;
                    const isGood = item.inverted ? d < 0 : d > 0;
                    return (
                      <div key={i} className="px-3 py-2.5 text-center hover:bg-gray-50/50 transition-colors">
                        <p className="text-[9px] font-medium text-gray-400 uppercase tracking-wider">{item.label}</p>
                        <p className={cn('text-lg font-black leading-none mt-0.5', item.color)}>{item.val}</p>
                        {d !== 0 && (
                          <span className={cn('inline-flex items-center gap-0.5 text-[9px] font-bold mt-0.5', isGood ? 'text-emerald-500' : 'text-red-400')}>
                            {d > 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                            {capWow(item.inverted ? d : d)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ═══ ROW 2: #1 Action — the HERO callout ═══ */}
            {ins.one_thing_to_change && (
              <div className="bg-amber-400 rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border-2 border-[#1a1a2e] flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-wider text-amber-900/70 mb-0.5">#1 Action This Week</p>
                  <p className="text-sm font-semibold text-[#1a1a2e] leading-snug">{ins.one_thing_to_change}</p>
                </div>
                {ins.kb_gaps?.length > 0 && (
                  <button onClick={() => navigate('/knowledge')} className="px-3 py-1.5 bg-white text-xs font-bold text-[#1a1a2e] rounded-lg border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all flex-shrink-0">
                    Fix it →
                  </button>
                )}
              </div>
            )}

            {/* ═══ ROW 3: Three Cards — Demand | Problems | Channels ═══ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* DEMAND — blue accent, left border strip */}
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
                <div className="border-l-4 border-blue-500 px-4 py-3">
                  <div className="flex items-center gap-2 mb-2.5">
                    <ShoppingBag className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-black uppercase tracking-wider text-blue-600">Demand</span>
                  </div>
                  {(s.top_demand?.length > 0) ? s.top_demand.slice(0, 4).map((d: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 py-1 border-b border-gray-50 last:border-0">
                      <span className="w-5 h-5 rounded-md bg-blue-100 text-blue-600 text-[10px] font-black flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <span className="text-xs text-gray-700 flex-1 truncate">{d.item}</span>
                      <span className="text-[10px] font-bold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">{d.count}x</span>
                    </div>
                  )) : (
                    <p className="text-xs text-gray-400 italic py-2">No order data yet</p>
                  )}
                  {ins.demand_trends?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-blue-100">
                      {ins.demand_trends.slice(0, 2).map((dt: any, i: number) => (
                        <p key={i} className="text-[10px] text-blue-500 flex items-center gap-1">
                          {dt.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : dt.trend === 'down' ? <TrendingDown className="w-3 h-3" /> : <Activity className="w-3 h-3" />}
                          <span className="font-medium">{dt.item}</span>: {dt.detail}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* PROBLEMS — red accent */}
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
                <div className="border-l-4 border-red-500 px-4 py-3">
                  <div className="flex items-center gap-2 mb-2.5">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-xs font-black uppercase tracking-wider text-red-600">Problems</span>
                  </div>
                  {(ins.problems?.length > 0 || ins.friction_points?.length > 0) ? (
                    (ins.problems || ins.friction_points || []).slice(0, 3).map((p: any, i: number) => (
                      <div key={i} className="py-1.5 border-b border-gray-50 last:border-0">
                        <div className="flex items-center gap-1.5">
                          <span className={cn('w-2 h-2 rounded-full flex-shrink-0', p.severity === 'high' ? 'bg-red-500' : p.severity === 'medium' ? 'bg-amber-400' : 'bg-gray-300')} />
                          <p className="text-xs font-semibold text-gray-900">{p.title}</p>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5 ml-3.5 line-clamp-2">{p.detail}</p>
                      </div>
                    ))
                  ) : s.complaints?.length > 0 ? (
                    s.complaints.slice(0, 2).map((c: any, i: number) => (
                      <div key={i} className="py-1.5 border-b border-gray-50 last:border-0">
                        <p className="text-xs font-semibold text-gray-900">{c.topic} ({c.count}x)</p>
                        <p className="text-[10px] text-red-500 mt-0.5 italic">"{c.sample}"</p>
                      </div>
                    ))
                  ) : (
                    <div className="py-3 text-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                      <p className="text-xs text-emerald-600 font-medium">All clear!</p>
                    </div>
                  )}
                </div>
              </div>

              {/* CHANNELS — purple accent with stacked bars */}
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
                <div className="border-l-4 border-violet-500 px-4 py-3">
                  <div className="flex items-center gap-2 mb-2.5">
                    <BarChart3 className="w-4 h-4 text-violet-500" />
                    <span className="text-xs font-black uppercase tracking-wider text-violet-600">Channels</span>
                  </div>
                  {(s.channel_breakdown?.length > 0) ? s.channel_breakdown.slice(0, 4).map((ch: any, i: number) => (
                    <div key={i} className="py-1 border-b border-gray-50 last:border-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-700 capitalize font-medium">{ch.channel_type}</span>
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="text-gray-400">{ch.conversations}</span>
                          <span className="font-bold text-violet-600 bg-violet-50 px-1 rounded">{ch.conversion_rate}%</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-violet-50 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-400 to-violet-500 rounded-full transition-all" style={{ width: `${(ch.conversations / maxCh) * 100}%` }} />
                      </div>
                    </div>
                  )) : (
                    <p className="text-xs text-gray-400 italic py-2">No channel data</p>
                  )}
                  {ins.channel_recommendation && (
                    <p className="text-[10px] text-violet-500 mt-2 pt-2 border-t border-violet-100 font-medium">{ins.channel_recommendation}</p>
                  )}
                </div>
              </div>
            </div>

            {/* ═══ ALWAYS VISIBLE: Questions + KB Gaps + Opportunities ═══ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Top Questions */}
              {ins.top_questions?.length > 0 && (
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4">
                  <h4 className="text-xs font-black uppercase tracking-wider text-gray-600 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-blue-400" /> Top Questions
                  </h4>
                  <div className="space-y-1.5">
                    {ins.top_questions.map((q: any, i: number) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <span className={cn('w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[9px] font-black border', q.answered_well ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-500 border-red-200')}>
                          {q.answered_well ? '✓' : '✗'}
                        </span>
                        <span className="text-xs text-gray-700 flex-1 truncate">{q.question}</span>
                        {q.count > 0 && <span className="text-[10px] font-bold text-gray-400">{q.count}x</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* KB Gaps */}
              {ins.kb_gaps?.length > 0 && (
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4">
                  <h4 className="text-xs font-black uppercase tracking-wider text-gray-600 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400" /> Knowledge Gaps
                  </h4>
                  <div className="space-y-2">
                    {ins.kb_gaps.map((g: any, i: number) => (
                      <div key={i} className="flex items-start justify-between gap-2 bg-amber-50/50 rounded-lg px-3 py-2 border border-amber-100">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-900">{g.topic}</p>
                          <p className="text-[10px] text-gray-500 line-clamp-1">{g.suggested_content}</p>
                        </div>
                        <button onClick={() => navigate('/knowledge')} className="text-[9px] font-bold text-amber-700 bg-amber-200 px-2 py-1 rounded-md border border-amber-300 hover:bg-amber-300 transition-colors flex-shrink-0">
                          Add →
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Opportunities */}
              {ins.opportunities?.length > 0 && (
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4">
                  <h4 className="text-xs font-black uppercase tracking-wider text-gray-600 mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-400" /> Opportunities
                  </h4>
                  <div className="space-y-2">
                    {ins.opportunities.slice(0, 3).map((o: any, i: number) => (
                      <div key={i} className="py-1.5 border-b border-gray-50 last:border-0">
                        <div className="flex items-center gap-1.5">
                          <span className={cn('w-2 h-2 rounded-full flex-shrink-0', o.impact === 'high' ? 'bg-emerald-500' : o.impact === 'medium' ? 'bg-blue-400' : 'bg-gray-300')} />
                          <p className="text-xs font-semibold text-gray-900">{o.title}</p>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5 ml-3.5 line-clamp-2">{o.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ═══ EXPANDABLE: Pipeline + Peak Hours + Sentiment ═══ */}
            {insightExpanded && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Pipeline Funnel */}
                {s.pipeline_completion?.length > 0 && (
                  <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4">
                    <h4 className="text-xs font-black uppercase tracking-wider text-gray-600 mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-gray-400" /> Pipeline Funnel
                    </h4>
                    <div className="space-y-2">
                      {s.pipeline_completion.slice(0, 6).map((p: any, i: number) => (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[11px] text-gray-600">{p.step_name}</span>
                            <span className={cn('text-[11px] font-black', p.completion_rate >= 70 ? 'text-emerald-600' : p.completion_rate >= 40 ? 'text-amber-500' : 'text-red-500')}>{p.completion_rate}%</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                            <div className={cn('h-full rounded-full transition-all', p.completion_rate >= 70 ? 'bg-emerald-400' : p.completion_rate >= 40 ? 'bg-amber-400' : 'bg-red-400')} style={{ width: `${Math.max(p.completion_rate, 2)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Peak Hours + Sentiment */}
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4">
                  <div className="grid grid-cols-2 gap-4">
                    {s.peak_hours?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-black uppercase tracking-wider text-gray-600 mb-2 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" /> Peak Hours
                        </h4>
                        <div className="space-y-1">
                          {s.peak_hours.slice(0, 4).map((h: any, i: number) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">{h.hour}:00</span>
                              <span className="text-xs font-bold text-[#1a1a2e]">{h.msg_count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {s.sentiment_summary?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-black uppercase tracking-wider text-gray-600 mb-2 flex items-center gap-2">
                          <Heart className="w-4 h-4 text-gray-400" /> Sentiment
                        </h4>
                        <div className="space-y-1">
                          {s.sentiment_summary.map((st: any, i: number) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className={cn('text-xs capitalize font-medium', st.sentiment === 'positive' ? 'text-emerald-600' : st.sentiment === 'negative' ? 'text-red-500' : 'text-gray-400')}>{st.sentiment}</span>
                              <span className="text-xs font-bold text-[#1a1a2e]">{st.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Expand/Collapse — only for pipeline/hours/sentiment */}
            <button
              onClick={() => setInsightExpanded(!insightExpanded)}
              className="w-full py-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-[#1a1a2e] transition-colors flex items-center justify-center gap-1"
            >
              {insightExpanded ? 'Hide pipeline & stats' : 'Show pipeline, peak hours & sentiment'}
              <ChevronDown className={cn('w-3 h-3 transition-transform', insightExpanded && 'rotate-180')} />
            </button>
          </div>
        );
      })() : (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 border-2 border-[#1a1a2e] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-[#1a1a2e]">Your Morning Briefing</h3>
                <p className="text-xs text-gray-500">Health score, demand signals, problems, and actions — from your conversations</p>
              </div>
            </div>
            <button
              onClick={handleGenerateInsight}
              disabled={insightLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400 text-[#1a1a2e] text-xs font-bold rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[4px_4px_0px_#1a1a2e] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all disabled:opacity-50 active:translate-x-0 active:translate-y-0 active:shadow-[1px_1px_0px_#1a1a2e]"
            >
              {insightLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {insightLoading ? 'Analyzing...' : 'Generate Briefing'}
            </button>
          </div>
        </div>
      )}

      {/* Main content row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Agents — large card */}
        <div className="lg:col-span-2 bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]">
          <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#1a1a2e]">
            <h3 className="text-sm font-semibold text-gray-900">Your Agents</h3>
            <button
              onClick={() => navigate('/agents')}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-0.5 transition-colors"
            >
              View all <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {recentAgents.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Bot className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">No agents yet</p>
              <p className="text-xs text-gray-500 mb-4">Create your first AI agent to get started</p>
              <button
                onClick={() => navigate('/agents/new')}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white text-xs font-medium rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Create Agent
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentAgents.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => navigate(`/agents/${agent.id}`)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/50 transition-colors text-left group"
                >
                  <div className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                    agent.status === 'active' ? 'bg-emerald-100' :
                    agent.status === 'testing' ? 'bg-amber-100' : 'bg-gray-100'
                  )}>
                    <Bot className={cn(
                      'w-4.5 h-4.5',
                      agent.status === 'active' ? 'text-emerald-600' :
                      agent.status === 'testing' ? 'text-amber-600' : 'text-gray-400'
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {agent.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      Updated {formatRelativeTime(agent.updated_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                      <span className={cn('w-1.5 h-1.5 rounded-full', getStatusDot(agent.status))} />
                      {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right sidebar — Quick Stats + Actions */}
        <div className="space-y-4">
          {/* Credit balance mini card */}
          {creditBalance !== null && (
            <div
              onClick={() => navigate('/credits')}
              className="bg-blue-500 rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 cursor-pointer hover:bg-blue-600 transition-all"
            >
              <p className="text-blue-200 text-xs font-medium">Available Credits</p>
              <p className="text-2xl font-bold text-white mt-1">${creditBalance.toFixed(2)}</p>
              <p className="text-blue-300 text-[11px] mt-1">Click to manage credits</p>
            </div>
          )}

          {/* More Stats */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Quick Stats</h3>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-gray-400" />
                  Escalation Rate
                </span>
                <span className="text-sm font-semibold text-gray-900">{(stats?.escalation_rate || 0).toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  Avg Response
                </span>
                <span className="text-sm font-semibold text-gray-900">{formatDuration(stats?.avg_response_time || 0)}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
            <div className="space-y-1.5">
              <button
                onClick={() => navigate('/agents/new')}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-blue-50 text-left transition-colors group"
              >
                <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <Plus className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-700 group-hover:text-blue-700 transition-colors">New Agent</span>
              </button>
              <button
                onClick={() => navigate('/knowledge')}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-emerald-50 text-left transition-colors group"
              >
                <div className="w-7 h-7 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                  <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-sm text-gray-700 group-hover:text-emerald-700 transition-colors">Upload Documents</span>
              </button>
              <button
                onClick={() => navigate('/channels')}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-purple-50 text-left transition-colors group"
              >
                <div className="w-7 h-7 rounded-md bg-purple-100 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                  <Radio className="w-3.5 h-3.5 text-purple-600" />
                </div>
                <span className="text-sm text-gray-700 group-hover:text-purple-700 transition-colors">Add Channel</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
